import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit, Trash2, X, ArrowLeft, Calendar as CalendarIcon, ExternalLink, Download, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ManageCalendars = () => {
  const { token, isAdmin, isSuperAdmin, user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [timezones, setTimezones] = useState([]);
  
  // State for creating calendar for a specific admin (passed from ManageUsers)
  const [createForAdmin, setCreateForAdmin] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    location: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    active_status: true,
    multiple_servers: false,
    booking_type: 'fixed',
    office_hours: '',
    min_advance_booking_hours: 24,
    max_advance_booking_days: 14,
    cancellation_cutoff_hours: 48,
    cancellation_policy: ''
  });
  const [importData, setImportData] = useState(null);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [archivesCount, setArchivesCount] = useState(0);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchCalendars();
    fetchPlanFeatures();
    fetchTimezones();
    
    // Check if we're creating a calendar for a specific admin
    if (location.state?.createForAdmin) {
      setCreateForAdmin(location.state.createForAdmin);
      // Auto-open the modal
      setShowModal(true);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, []);

  const fetchPlanFeatures = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me/plan-usage`, {
        headers: getAuthHeaders()
      });
      if (response.data?.data?.features) {
        setPlanFeatures(response.data.data.features);
      }
    } catch (err) {
      console.error('Error fetching plan features:', err);
    }
  };

  const fetchTimezones = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars/timezones`, {
        headers: getAuthHeaders()
      });
      if (response.data?.data) {
        setTimezones(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching timezones:', err);
      // Fallback to common timezones
      setTimezones([
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Asia/Shanghai',
        'Australia/Sydney'
      ]);
    }
  };

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/calendars`, {
        headers: getAuthHeaders()
      });
      setCalendars(response.data.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching calendars:', err);
      setError('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to sanitize calendar name for URL-friendly symbolic name
  const sanitizeSymbolicName = (name) => {
    return name
      .toLowerCase()                    // Convert to lowercase
      .replace(/[^a-z0-9\s-]/g, '')    // Remove invalid characters (keep letters, numbers, spaces, hyphens)
      .trim()                           // Remove leading/trailing spaces
      .replace(/\s+/g, '-')            // Replace spaces with hyphens
      .replace(/-+/g, '-')             // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, '');          // Remove leading/trailing hyphens
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  const openAddModal = () => {
    setEditingCalendar(null);
    setFormData({
      name: '',
      type: '',
      location: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      active_status: true,
      multiple_servers: false,
      booking_type: 'fixed',
      office_hours: '',
      min_advance_booking_hours: 24,
      max_advance_booking_days: 14,
      cancellation_cutoff_hours: 48,
      cancellation_policy: ''
    });
    setImportData(null);
    setSelectedArchive(null);
    setShowImportOptions(false);
    fetchArchives(); // Load archives list
    setShowModal(true);
  };

  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [showArchiveNameModal, setShowArchiveNameModal] = useState(false);
  const [archiveNameInput, setArchiveNameInput] = useState('');
  const [calendarToExport, setCalendarToExport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState(null);
  const [exportBeforeDelete, setExportBeforeDelete] = useState(false);

  const fetchArchives = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars/archives`, {
        headers: getAuthHeaders()
      });
      const archivesList = response.data.data || [];
      setArchives(archivesList);
      setArchivesCount(archivesList.length);
    } catch (err) {
      console.error('Error fetching archives:', err);
      setArchives([]);
      setArchivesCount(0);
    }
  };

  // Fetch archives count on mount to show/hide button
  useEffect(() => {
    const checkArchives = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/calendars/archives`, {
          headers: getAuthHeaders()
        });
        setArchivesCount((response.data.data || []).length);
      } catch (err) {
        setArchivesCount(0);
      }
    };
    checkArchives();
  }, []);

  const handleSelectArchive = async (archiveId) => {
    if (!archiveId) {
      setSelectedArchive(null);
      setImportData(null);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/calendars/archives/${archiveId}`, {
        headers: getAuthHeaders()
      });
      
      const archive = response.data.data;
      const imported = archive.export_data;
      
      setSelectedArchive(archive);
      setImportData(archive);
      setShowImportOptions(true);
      
      // Pre-fill form with imported calendar data
      setFormData({
        ...formData,
        type: imported.calendar.type || '',
        location: imported.calendar.location || '',
        timezone: imported.calendar.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        booking_type: imported.calendar.booking_type || 'fixed',
        office_hours: imported.calendar.office_hours || '',
        min_advance_booking_hours: imported.calendar.min_advance_booking_hours || 24,
        max_advance_booking_days: imported.calendar.max_advance_booking_days || 14,
        cancellation_cutoff_hours: imported.calendar.cancellation_cutoff_hours || 48,
        cancellation_policy: imported.calendar.cancellation_policy || ''
      });
    } catch (err) {
      console.error('Error loading archive:', err);
    }
  };

  const openEditModal = (calendar) => {
    setEditingCalendar(calendar);
    setFormData({
      name: calendar.name,
      type: calendar.type,
      location: calendar.location,
      timezone: calendar.timezone || 'UTC',
      active_status: calendar.active_status,
      multiple_servers: calendar.multiple_servers || false,
      booking_type: calendar.booking_type || 'fixed',
      office_hours: calendar.office_hours,
      min_advance_booking_hours: calendar.min_advance_booking_hours,
      max_advance_booking_days: calendar.max_advance_booking_days,
      cancellation_cutoff_hours: calendar.cancellation_cutoff_hours,
      cancellation_policy: calendar.cancellation_policy || ''
    });
    // Store the original symbolic_name for display (read-only)
    setEditingCalendar({ ...calendar });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCalendar(null);
    setCreateForAdmin(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingCalendar) {
        // Update existing calendar - exclude name (immutable)
        const { name, ...updateData } = formData;
        await axios.put(
          `${API_BASE_URL}/calendars/${editingCalendar.id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
      } else if (selectedArchive) {
        // Import calendar from archive
        const response = await axios.post(
          `${API_BASE_URL}/calendars/import`,
          null,
          {
            params: {
              archive_id: selectedArchive.id,
              new_calendar_name: formData.name,
              new_symbolic_name: sanitizeSymbolicName(formData.name),
              import_services: true,
              import_servers: true,
              import_availability: true,
              import_future_bookings: true,
              import_history: false
            },
            headers: getAuthHeaders()
          }
        );
        
        // Show success notification
        const notification = document.createElement('div');
        notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
        notification.innerHTML = `
          <div style="font-weight:bold;margin-bottom:8px;">✅ Import Successful!</div>
          <div style="font-size:14px;">Imported: ${response.data.imported.services} services, ${response.data.imported.appointments} appointments</div>
        `;
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 5000);
      } else {
        // Create new calendar
        // If creating for a specific admin (Super Admin feature), include owner_id
        const calendarData = { ...formData };
        if (createForAdmin?.id) {
          calendarData.owner_id = createForAdmin.id;
        }
        
        await axios.post(
          `${API_BASE_URL}/calendars`,
          calendarData,
          { headers: getAuthHeaders() }
        );
        
        // If we created for a specific admin, navigate back to Manage Calendar Admins
        if (createForAdmin) {
          navigate('/admin/users', { 
            state: { message: `Calendar "${formData.name}" created successfully for ${createForAdmin.full_name}` }
          });
          return;
        }
      }
      
      closeModal();
      fetchCalendars();
    } catch (err) {
      console.error('Error saving calendar:', err);
      const errorDetail = err.response?.data?.detail;
      
      // Handle Pydantic validation errors (array of error objects)
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(err => err.msg || 'Validation error').join(', ');
        setError(errorMessages || 'Please check your input and try again.');
      } else {
        setError(errorDetail || 'Failed to save calendar');
      }
    }
  };

  const handleDeleteClick = (calendar) => {
    setCalendarToDelete(calendar);
    setExportBeforeDelete(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!calendarToDelete) return;

    setShowDeleteModal(false);

    try {
      setError('');
      
      // Export first if requested
      if (exportBeforeDelete) {
        const archiveName = `${calendarToDelete.name} - Backup before deletion`;
        await axios.post(
          `${API_BASE_URL}/calendars/${calendarToDelete.id}/export`,
          null,
          {
            params: {
              archive_name: archiveName,
              include_future_bookings: true,
              include_history: true,
              history_days: 365
            },
            headers: getAuthHeaders()
          }
        );
        
        // Update archives count
        setArchivesCount(prevCount => prevCount + 1);
        
        console.log('✅ Calendar exported before deletion');
      }
      
      // Delete calendar
      const response = await axios.delete(`${API_BASE_URL}/calendars/${calendarToDelete.id}`, {
        headers: getAuthHeaders()
      });
      
      console.log('Delete response:', response.data);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
      notification.innerHTML = `
        <div style="font-weight:bold;margin-bottom:8px;">✅ Calendar Deleted</div>
        ${exportBeforeDelete ? '<div style="font-size:14px;">Backup saved to archives</div>' : ''}
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 4000);
      
      // Refresh the calendar list
      await fetchCalendars();
    } catch (err) {
      console.error('Error deleting calendar:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete calendar';
      setError(errorMessage);
      
      // Show error notification
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#ef4444;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
      notification.innerHTML = `
        <div style="font-weight:bold;margin-bottom:8px;">❌ Delete Failed</div>
        <div style="font-size:14px;">${errorMessage}</div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 5000);
    }
  };

  const handleOpenCalendar = (calendarId) => {
    navigate(`/calendar/${calendarId}`);
  };

  const handleExport = (calendar) => {
    console.log('🔥🔥🔥 NEW ARCHIVE VERSION 2.0 LOADED 🔥🔥🔥');
    setCalendarToExport(calendar);
    setArchiveNameInput('');
    setShowArchiveNameModal(true);
  };

  const handleConfirmExport = async () => {
    const calendar = calendarToExport;
    const archiveName = archiveNameInput.trim() || null;
    
    setShowArchiveNameModal(false);
    
    console.log('Export clicked for calendar:', calendar.name, calendar.id);
    
    try {
      console.log('Saving to archives...');
      const response = await axios.post(
        `${API_BASE_URL}/calendars/${calendar.id}/export`,
        null,
        {
          params: {
            archive_name: archiveName,
            include_future_bookings: true,
            include_history: true,
            history_days: 365
          },
          headers: getAuthHeaders()
        }
      );

      console.log('✅ Export saved to archives:', response.data);
      
      // Update archives count to show the button
      setArchivesCount(prevCount => prevCount + 1);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
      notification.innerHTML = `
        <div style="font-weight:bold;margin-bottom:8px;">✅ Export Successful!</div>
        <div style="font-size:14px;">Calendar saved to archives</div>
        <div style="font-size:12px;margin-top:8px;opacity:0.9;">Archive: ${response.data.archive_name}</div>
        <div style="font-size:12px;margin-top:4px;opacity:0.9;">"Manage Archives" button now visible above</div>
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.remove();
      }, 5000);
      
    } catch (err) {
      console.error('❌ Error exporting calendar:', err);
      console.error('Error details:', err.response);
      
      // Show error notification
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#ef4444;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
      notification.innerHTML = `
        <div style="font-weight:bold;margin-bottom:8px;">❌ Export Failed</div>
        <div style="font-size:14px;">${err.response?.data?.detail || err.message}</div>
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.remove();
      }, 5000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Manage Calendars</h1>
              <p className="text-gray-600 mt-2">Create and manage your scheduling calendars</p>
            </div>
            <div className="flex space-x-3">
              {archivesCount > 0 && (
                <button
                  onClick={() => navigate('/admin/archives')}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-md transition-colors flex items-center"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Manage Archives ({archivesCount})
                </button>
              )}
              {/* 
                CALENDAR LIMIT CHECK (Jan 15, 2026):
                - Free/Starter/Pro plans have single_calendar=true, multiple_calendars=false
                - Business plan has multiple_calendars=true
                - If plan doesn't allow multiple calendars AND user already has 1+ calendar, disable Add button
              */}
              {planFeatures && !planFeatures.multiple_calendars && calendars.length >= 1 ? (
                <div className="flex items-center gap-2">
                  <button
                    disabled
                    className="bg-gray-300 text-gray-500 font-semibold px-6 py-3 rounded-md cursor-not-allowed flex items-center"
                    title="Your plan only allows one calendar"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Calendar
                  </button>
                  <a 
                    href="/pricing" 
                    className="text-sm text-yellow-600 hover:text-yellow-700 underline whitespace-nowrap"
                  >
                    Upgrade for more calendars
                  </a>
                </div>
              ) : (
                <button
                  onClick={openAddModal}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-md transition-colors flex items-center"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Calendar
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {calendars.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-4">No calendars created yet</p>
              <button
                onClick={openAddModal}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-2 rounded-md transition-colors"
              >
                Create Your First Calendar
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    {isSuperAdmin() && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Calendar Owner
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Booking Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timezone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Multiple Servers
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {calendars.map((calendar) => (
                    <tr key={calendar.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {calendar.name}
                          </span>
                          {calendar.symbolic_name && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                                /{calendar.symbolic_name}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const url = `${window.location.origin}/calendar/${calendar.symbolic_name}`;
                                  navigator.clipboard.writeText(url);
                                  // Show brief feedback
                                  const btn = e.currentTarget;
                                  btn.title = 'Copied!';
                                  setTimeout(() => btn.title = 'Copy URL', 1500);
                                }}
                                className="text-gray-400 hover:text-gray-600 p-0.5"
                                title="Copy URL"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      {isSuperAdmin() && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {calendar.owner_name || 'Unknown'}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {calendar.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          calendar.booking_type === 'floating'
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-indigo-100 text-indigo-800'
                        }`}>
                          {calendar.booking_type === 'floating' ? 'Floating' : 'Fixed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {calendar.location}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {(calendar.timezone || 'UTC').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          calendar.multiple_servers 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {calendar.multiple_servers ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          calendar.active_status 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {calendar.active_status ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenCalendar(calendar.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Open Calendar"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/calendars/edit/${calendar.id}`)}
                          className="text-yellow-600 hover:text-yellow-900 mr-3"
                          title="Edit Calendar"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleExport(calendar)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Export Calendar"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(calendar)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Calendar"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <Footer />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingCalendar ? 'Edit Calendar' : 'Add New Calendar'}
                </h2>
                {createForAdmin && !editingCalendar && (
                  <p className="text-sm text-yellow-600 mt-1">
                    Creating calendar for: <span className="font-semibold">{createForAdmin.full_name}</span> ({createForAdmin.email})
                  </p>
                )}
              </div>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}

              {/* Import from Archive Option (only for new calendars and only when archives exist) */}
              {!editingCalendar && archives.length > 0 && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Upload className="w-4 h-4 inline mr-2" />
                    Import from Archive (Optional)
                  </label>
                  <select
                    value={selectedArchive?.id || ''}
                    onChange={(e) => handleSelectArchive(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">-- Start Fresh (No Import) --</option>
                    {archives.map(archive => (
                      <option key={archive.id} value={archive.id}>
                        {archive.name} ({archive.original_calendar_name}) - {new Date(archive.created_at).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Select an archived export to pre-fill settings and import data
                  </p>
                  {selectedArchive && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                      ✓ Archive loaded: {selectedArchive.export_data?.metadata?.total_services || 0} services, 
                      {selectedArchive.export_data?.metadata?.total_servers || 0} servers, 
                      {selectedArchive.export_data?.metadata?.total_appointments || 0} appointments
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Calendar Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${editingCalendar ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="e.g., Main Salon Calendar"
                  required
                  disabled={!!editingCalendar}
                />
                {editingCalendar && (
                  <p className="mt-1 text-xs text-orange-600">
                    Calendar name cannot be changed after creation.
                  </p>
                )}
              </div>

              {/* Show symbolic name as read-only info when editing */}
              {editingCalendar && editingCalendar.symbolic_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL (Symbolic Name)
                  </label>
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600">
                    {editingCalendar.symbolic_name}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    This URL is auto-generated from the calendar name and cannot be changed.
                  </p>
                </div>
              )}

              {/* Show preview of what slug will be for new calendars */}
              {!editingCalendar && formData.name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Preview
                  </label>
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600">
                    {sanitizeSymbolicName(formData.name) || '(enter calendar name)'}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    This URL will be auto-generated from your calendar name.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type *
                </label>
                <input
                  type="text"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Hair Services"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., 123 Main St, New York, NY"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone *
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  required
                >
                  {/* Show browser detected timezone first if it's in the list */}
                  {(() => {
                    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const browserTzInList = timezones.includes(browserTz);
                    
                    if (browserTzInList) {
                      return (
                        <>
                          <option key={`detected-${browserTz}`} value={browserTz}>
                            ✓ {browserTz.replace(/_/g, ' ')} (Your timezone)
                          </option>
                          <option disabled>──────────────</option>
                          {timezones.filter(tz => tz !== browserTz).map((tz) => (
                            <option key={tz} value={tz}>
                              {tz.replace(/_/g, ' ')}
                            </option>
                          ))}
                        </>
                      );
                    } else {
                      return timezones.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz.replace(/_/g, ' ')}
                        </option>
                      ));
                    }
                  })()}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {!editingCalendar && formData.timezone === Intl.DateTimeFormat().resolvedOptions().timeZone ? (
                    <span className="text-green-600">
                      ✓ Auto-detected from your browser. All appointment times will be displayed in this timezone.
                    </span>
                  ) : (
                    'All appointment times will be displayed in this timezone'
                  )}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Office Hours *
                </label>
                <input
                  type="text"
                  name="office_hours"
                  value={formData.office_hours}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., 9 AM - 5 PM"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Type *
                </label>
                {editingCalendar ? (
                  // Edit mode - show as read-only
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 flex items-center justify-between">
                    <span>
                      {formData.booking_type === 'fixed' ? 'Fixed (Top-of-Hour)' : 'Floating (15-min Intervals)'}
                    </span>
                    <span className="text-xs text-gray-500 italic">
                      Cannot be changed after creation
                    </span>
                  </div>
                ) : (
                  // Create mode - show as dropdown
                  <select
                    name="booking_type"
                    value={formData.booking_type}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                      planFeatures && !planFeatures.floating_calendar ? 'bg-gray-100' : ''
                    }`}
                    required
                  >
                    <option value="fixed">Fixed (Top-of-Hour) - Hourly slots</option>
                    <option value="floating" disabled={planFeatures && !planFeatures.floating_calendar}>
                      Floating (15-min Intervals) - Flexible start times
                      {planFeatures && !planFeatures.floating_calendar ? ' (Upgrade Required)' : ''}
                    </option>
                  </select>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {editingCalendar ? (
                    'Booking type is set at creation and cannot be changed'
                  ) : formData.booking_type === 'fixed' ? (
                    'Appointments start at top of each hour (9:00, 10:00, 11:00...)'
                  ) : (
                    'Appointments can start at any 15-minute interval (9:00, 9:15, 9:30...)'
                  )}
                </p>
                {planFeatures && !planFeatures.floating_calendar && !editingCalendar && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚡ Floating calendar requires Starter plan or higher. <a href="/pricing" className="underline">Upgrade now</a>
                  </p>
                )}
              </div>

              <div>
                <label className={`flex items-center ${planFeatures && !planFeatures.multiple_servers ? 'opacity-60' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    name="multiple_servers"
                    checked={formData.multiple_servers || false}
                    onChange={(e) => setFormData({...formData, multiple_servers: e.target.checked})}
                    disabled={planFeatures && !planFeatures.multiple_servers}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Enable Multiple Servers
                    {planFeatures && !planFeatures.multiple_servers && (
                      <span className="text-orange-500 ml-2">(Business Plan Required)</span>
                    )}
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2 ml-7">
                  Allow multiple staff/servers to be assigned to this calendar for concurrent bookings
                </p>
                {planFeatures && !planFeatures.multiple_servers && (
                  <p className="text-xs text-orange-600 mt-1 ml-7">
                    ⚡ Multiple servers requires Business plan. <a href="/pricing" className="underline">Upgrade now</a>
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Advance Booking (hours) *
                  </label>
                  <input
                    type="number"
                    name="min_advance_booking_hours"
                    value={formData.min_advance_booking_hours}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Advance Booking (days) *
                  </label>
                  <input
                    type="number"
                    name="max_advance_booking_days"
                    value={formData.max_advance_booking_days}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Online Cancellation Cutoff (hours) *
                  </label>
                  <input
                    type="number"
                    name="cancellation_cutoff_hours"
                    value={formData.cancellation_cutoff_hours}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Policy
                </label>
                <textarea
                  name="cancellation_policy"
                  value={formData.cancellation_policy}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter your cancellation policy..."
                  rows="3"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="active_status"
                  checked={formData.active_status}
                  onChange={handleChange}
                  className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Active (customers can browse and book)
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors"
                >
                  {editingCalendar ? 'Update Calendar' : 'Create Calendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive Name Modal */}
      {showArchiveNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Export to Archive</h3>
              <p className="text-gray-600 mb-4">Enter a name for this archive (optional):</p>
              <input
                type="text"
                value={archiveNameInput}
                onChange={(e) => setArchiveNameInput(e.target.value)}
                placeholder="Leave empty for auto-generated name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmExport();
                  }
                }}
              />
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowArchiveNameModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmExport}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md"
                >
                  Export to Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Calendar Confirmation Modal */}
      {showDeleteModal && calendarToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="ml-4 text-xl font-bold text-gray-900">Delete Calendar</h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                Are you sure you want to permanently delete this calendar?
              </p>
              
              <div className="bg-gray-50 rounded-md p-4 mb-4">
                <p className="font-semibold text-gray-900 text-lg">{calendarToDelete.name}</p>
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <p>📍 Location: {calendarToDelete.location}</p>
                  <p>📝 Type: {calendarToDelete.type}</p>
                  <p>⏰ Booking Type: {calendarToDelete.booking_type === 'floating' ? 'Floating (15-min)' : 'Fixed (Hourly)'}</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-700 text-lg">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700 font-medium">
                      This will permanently delete:
                    </p>
                    <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                      <li>All appointments and bookings</li>
                      <li>All availability schedules</li>
                      <li>Service and server associations</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportBeforeDelete}
                    onChange={(e) => setExportBeforeDelete(e.target.checked)}
                    className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm">
                    <span className="font-medium text-gray-900">Export to archive before deleting</span>
                    <p className="text-gray-500 mt-1">
                      Save a backup copy to archives so you can restore or reference it later
                    </p>
                  </span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md"
                >
                  {exportBeforeDelete ? 'Export & Delete' : 'Delete Calendar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCalendars;
